import { lineupOf } from '../game';
import { fieldGoalDistance, fieldGoalOdds, kickerSlot } from '../kick';
import type { Balance } from '../balance';
import { effectiveStat } from '../matchup';
import type { ConversionChoice, FourthDownChoice, Game } from '../types';
import { margin, noise, type AiContext } from './context';

/**
 * Fourth down and the try (slice 7): one expected-points table (balance.json
 * `coach.ai.fourthDown`, `coach.ai.twoPoint`) for every level; Rookie and Pro read it through
 * their noise. Points are the offense's, net of what the other team then expects.
 */

/** Expected points of a 1st down at `ballOn`, interpolated in `coach.ai.fourthDown.expectedPoints`. */
export function expectedPoints(ballOn: number, ctx: Pick<AiContext, 'b'>): number {
  const t = ctx.b.coach.ai.fourthDown.expectedPoints as readonly (readonly number[])[];
  const x = Math.min(Math.max(ballOn, t[0]?.[0] ?? 0), t.at(-1)?.[0] ?? 100);
  for (let i = 1; i < t.length; i++) {
    const [x1, y1] = t[i] as [number, number];
    const [x0, y0] = t[i - 1] as [number, number];
    if (x <= x1) return y0 + ((y1 - y0) * (x - x0)) / Math.max(1e-9, x1 - x0);
  }
  return (t.at(-1) as [number, number])[1];
}

/** The chance of converting with `toGo` yards to go: the first row whose most-yards covers it. */
export function convertChance(toGo: number, ctx: Pick<AiContext, 'b'>): number {
  const t = ctx.b.coach.ai.fourthDown.convert as readonly (readonly number[])[];
  for (const [most, p] of t as [number, number][]) if (toGo <= most) return p;
  return (t.at(-1) as [number, number])[1];
}

/** The offense's kicker's Sp. Atk as it plays now. */
function kickerSpa(game: Readonly<Game>, b: Balance): number {
  const team = game.teams[game.possession];
  const lineup = lineupOf(team, team.offense);
  return effectiveStat(lineup[kickerSlot(lineup, b)], 'spa', b);
}

/** Expected points of going for it, kicking the field goal, and punting from this 4th down. */
export function fourthDownValues(
  game: Readonly<Game>,
  ctx: Pick<AiContext, 'b'>,
): Record<FourthDownChoice, number> {
  const F = ctx.b.football;
  const T = ctx.b.coach.ai.fourthDown;
  const ep = (x: number) => expectedPoints(x, ctx);
  const afterScore = -ep(F.kickoffStartYardLine);
  const c = convertChance(game.toGo, ctx);
  const gained = game.ballOn + game.toGo;
  const success = gained >= 100 ? T.touchdownPoints + afterScore : ep(gained);
  const go = c * success - (1 - c) * ep(100 - game.ballOn);
  const q = fieldGoalOdds(
    kickerSpa(game, ctx.b),
    fieldGoalDistance(game.ballOn, ctx.b),
    false,
    ctx.b,
  );
  const fg =
    q * (F.scoring.fieldGoal + afterScore) -
    (1 - q) * ep(Math.max(F.missedFieldGoalYardLine, 100 - game.ballOn));
  const lands = game.ballOn + T.puntNetYards;
  const punt = lands >= 100 ? -ep(F.touchbackYardLine) : -ep(100 - lands);
  return { go, 'field-goal': fg, punt };
}

/**
 * The 4th-down call: the best of the three in expected points (plus the level's noise), after the
 * score-and-time rules: late (last quarter inside lateSeconds, or overtime) a field goal that does
 * not tie or take the lead is off; trailing inside noPuntSeconds, or in overtime, a punt is off.
 */
export function fourthDownCall(game: Readonly<Game>, ctx: AiContext): FourthDownChoice {
  const F = ctx.b.football;
  const T = ctx.b.coach.ai.fourthDown;
  const values = fourthDownValues(game, ctx);
  const m = margin(game, game.possession);
  const ot = game.quarter > F.quarters;
  const last = game.quarter === F.quarters;
  const allowed: FourthDownChoice[] = ['go'];
  if (!((ot || (last && game.clock <= T.lateSeconds)) && m + F.scoring.fieldGoal < 0)) {
    allowed.push('field-goal');
  }
  if (!ot && !(last && game.clock <= T.noPuntSeconds && m < 0)) allowed.push('punt');
  let best: FourthDownChoice = 'go';
  let top = -Infinity;
  for (const choice of allowed) {
    const v = values[choice] + noise(ctx, ctx.b.coach.ai.jitterScale.fourthDown);
    if (v > top) {
      top = v;
      best = choice;
    }
  }
  return best;
}

/**
 * The try: the extra point's odds x its point against coach.ai.twoPoint.convert x 2 (plus noise);
 * in the last quarter and overtime a margin in lateMargins (after the touchdown) always goes for two.
 */
export function conversionCall(game: Readonly<Game>, ctx: AiContext): ConversionChoice {
  const F = ctx.b.football;
  const T = ctx.b.coach.ai.twoPoint;
  const m = margin(game, game.possession);
  if (game.quarter >= F.quarters && (T.lateMargins as readonly number[]).includes(m)) {
    return 'two-point';
  }
  const kickOdds = fieldGoalOdds(
    kickerSpa(game, ctx.b),
    fieldGoalDistance(100 - F.extraPointYardLine, ctx.b),
    false,
    ctx.b,
  );
  const kick =
    kickOdds * F.scoring.extraPointKick + noise(ctx, ctx.b.coach.ai.jitterScale.fourthDown);
  const two = T.convert * F.scoring.twoPoint + noise(ctx, ctx.b.coach.ai.jitterScale.fourthDown);
  return two > kick ? 'two-point' : 'kick';
}
